<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';

import { BackgroundVideo } from '@sg-core/src/View/Components/Organisms/BackgroundVideo/background-video';

import { PhxsgSlider } from '../Slider';

import type { Swiper as SwiperType, SwiperOptions } from 'swiper/types';

const LOOP_SLIDE_SETS = 3;

export interface PhxsgProgressSliderProps {
  label?: string;
  autoplay: boolean;
  autoplayDelay: number;
  loop: boolean;
}

const { label, autoplay, autoplayDelay, loop } = defineProps<PhxsgProgressSliderProps>();

const rootRef = ref<HTMLElement | null>(null);
const progressRef = ref<HTMLElement | null>(null);
const swiperInstance = ref<SwiperType | null>(null);
const progress = ref(0);
const progressDuration = ref('0ms');

let slideCount = 0;
let transitionDuration = 0;

const slideSets = computed(() => (loop ? LOOP_SLIDE_SETS : 1));

const sliderOptions = computed<SwiperOptions>(() => ({
  autoplay: autoplay ? { delay: autoplayDelay } : false,
  freeMode: {
    enabled: true,
    sticky: true,
    momentumRatio: 0.025,
  },
  loop,
  slideToClickedSlide: true,
}));

const currentIndex = (swiper: SwiperType) => (slideCount > 0 ? swiper.realIndex % slideCount : 0);

const updateContentVisibility = (swiper: SwiperType) => {
  const items = rootRef.value?.querySelectorAll<HTMLElement>('.phxsg-progress-slider__item');

  slideCount = items?.length ?? 0;

  items?.forEach((item) => {
    const isActive = Number(item.dataset.index) === currentIndex(swiper);

    item.classList.toggle('phxsg-progress-slider__item--active', isActive);
    item.setAttribute('aria-hidden', String(!isActive));
    item.inert = !isActive;
  });
};

const setupClonedSlides = (swiper: SwiperType) => {
  swiper.slides.forEach((slide) => {
    const index = Number(slide.dataset.swiperSlideIndex);

    if (index < slideCount) {
      return;
    }

    slide.setAttribute('aria-hidden', 'true');
    slide.querySelectorAll<HTMLElement>('[id]').forEach((element) => {
      element.id = `${element.id}-clone-${index}`;
    });
    slide.querySelectorAll<HTMLElement>('.phxsg-background-video').forEach((element) => new BackgroundVideo(element));
  });
};

const clamp = (value: number) => Math.min(Math.max(value, 0), 1);

const progressAtIndex = (index: number) => (slideCount > 0 ? Math.min(index / slideCount, 1) : 0);

const loopPosition = (swiper: SwiperType) => {
  const translate = swiper.rtlTranslate ? swiper.translate : -swiper.translate;
  const index = Math.max(
    swiper.slidesGrid.findLastIndex((offset) => offset <= translate + 1),
    0,
  );
  const start = swiper.slidesGrid[index];
  const end = swiper.slidesGrid[index + 1] ?? start;
  const offset = end > start ? clamp((translate - start) / (end - start)) : 0;

  return (Number(swiper.slides[index]?.dataset.swiperSlideIndex ?? 0) % slideCount) + offset;
};

const updateProgress = (swiper: SwiperType) => {
  if (swiper.autoplay.running) {
    return;
  }

  const slidePosition = loop ? loopPosition(swiper) : clamp(swiper.progress) * (slideCount - 1);
  const isWrapping = slidePosition > slideCount - 1;
  const position = (autoplay ? 0 : 1) + (isWrapping ? currentIndex(swiper) : slidePosition);
  const duration = isWrapping ? swiper.params.speed : transitionDuration || (autoplay ? swiper.params.speed : 0);

  progress.value = progressAtIndex(position);
  progressDuration.value = `${duration}ms`;
};

const restartAutoplay = async (swiper: SwiperType) => {
  await nextTick();

  if (
    !autoplay ||
    swiper.touchEventsData.isTouched ||
    swiper.animating ||
    swiper.autoplay.running ||
    Math.abs(swiper.getTranslate() + swiper.snapGrid[swiper.snapIndex]) > 1 ||
    progressRef.value!.getAnimations({ subtree: true }).length
  ) {
    return;
  }

  swiper.autoplay.start();
  if (swiper.autoplay.paused) {
    swiper.autoplay.resume();
  }
};

const stopAutoplay = (swiper: SwiperType) => {
  swiper.autoplay.stop();
  updateProgress(swiper);
};

const onSwiper = async (swiper: SwiperType) => {
  swiperInstance.value = swiper;
  swiper.on('slideChange', updateContentVisibility);
  swiper.on('autoplayTimeLeft', (swiper, _timeLeft, percentage) => {
    if (swiper.autoplay.paused) {
      return;
    }

    progress.value = progressAtIndex(currentIndex(swiper) + 1 - clamp(percentage));
    progressDuration.value = '0ms';
  });
  swiper.on('setTranslate', updateProgress);
  swiper.on('setTransition', (_, duration) => {
    transitionDuration = duration;
  });
  swiper.on('touchStart', stopAutoplay);
  swiper.on('touchEnd', restartAutoplay);
  swiper.on('scroll', (swiper) => {
    stopAutoplay(swiper);
    void restartAutoplay(swiper);
  });
  swiper.on('transitionEnd', restartAutoplay);

  await nextTick();
  updateContentVisibility(swiper);
  setupClonedSlides(swiper);
  updateProgress(swiper);
};
</script>

<template>
  <div
    ref="rootRef"
    class="phxsg-progress-slider__carousel"
    role="group"
    aria-roledescription="carousel"
    :aria-label="label"
  >
    <div class="phxsg-progress-slider__slider-wrapper">
      <PhxsgSlider
        class="phxsg-progress-slider__slider"
        :class="{ 'phxsg-progress-slider__slider--loop': loop }"
        v-bind="sliderOptions"
        :navigation="false"
        :pagination="false"
        :scrollbar="false"
        @swiper="onSwiper"
      >
        <slot v-for="set in slideSets" :key="set" name="slides" />
      </PhxsgSlider>

      <div class="phxsg-progress-slider__content-wrapper">
        <div
          v-if="swiperInstance"
          ref="progressRef"
          class="phxsg-progress-slider__progress"
          :style="{ '--this-progress-slider-progress': progress, transitionDuration: progressDuration }"
          @transitionend.self="restartAutoplay(swiperInstance)"
        />

        <div class="phxsg-progress-slider__content">
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>

<style src="./progress-slider.css" />
